import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FolderPlus, MessageSquare, Plus, RotateCw, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import VoiceRecorder from '@/components/ui/VoiceRecorder';
import { errorText, keepAwake, myRequests, sendRequest, uploadRequestFile, type ClientRequest, type RequestKind } from '@/lib/requests';
import { cn } from '@/lib/cn';

/** English label with its Arabic under it: the portal is read by Arabic-first doctors and staff. */
export function Bi({ en, ar, className }: { en: string; ar: string; className?: string }) {
  return (
    <span className={cn('block', className)}>
      <span className="block">{en}</span>
      <span className="block text-[12px] text-muted font-normal" dir="rtl" lang="ar">{ar}</span>
    </span>
  );
}

export const STATUS: Record<string, { en: string; ar: string; tone: string }> = {
  new: { en: 'Received', ar: 'تم الاستلام', tone: 'bg-accent-600/15 text-accent-400' },
  approved: { en: 'Approved', ar: 'تمت الموافقة', tone: 'bg-emerald-500/15 text-emerald-400' },
  converted: { en: 'In production', ar: 'قيد التنفيذ', tone: 'bg-emerald-500/15 text-emerald-400' },
  declined: { en: 'Not planned', ar: 'تعذّر التنفيذ', tone: 'bg-rose-500/15 text-rose-400' },
};

const FOLDERS: { en: string; ar: string }[] = [
  { en: 'General', ar: 'عام' }, { en: 'Photos', ar: 'صور' }, { en: 'Videos', ar: 'فيديوهات' },
  { en: 'Logo and brand', ar: 'الشعار والهوية' }, { en: 'Before and after', ar: 'قبل وبعد' },
];
const DRAFT = 'kafreq:draft';

interface Pick { key: string; file: File; folder: string; pct: number; path?: string; error?: string }

export default function PortalRequestsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [kind, setKind] = useState<RequestKind | null>(params.get('new') === '1' ? null : null);
  const [composing, setComposing] = useState(params.get('new') === '1');
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [voice, setVoice] = useState<{ path: string; url: string } | null>(null);
  const [folder, setFolder] = useState('General');
  const [folders, setFolders] = useState(FOLDERS);
  const [picks, setPicks] = useState<Pick[]>([]);
  const [consent, setConsent] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const q = useQuery({ queryKey: ['portal-requests'], queryFn: myRequests, refetchInterval: 30000 });

  // a reload keeps what was written and what already finished uploading
  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT) || 'null');
      if (d && d.kind) { setKind(d.kind); setText(d.text || ''); setTitle(d.title || ''); setComposing(true); }
    } catch { /* none */ }
  }, []);
  useEffect(() => {
    if (!composing || !kind) return;
    try { localStorage.setItem(DRAFT, JSON.stringify({ kind, text, title })); } catch { /* fine */ }
  }, [composing, kind, text, title]);

  const uploading = picks.some((p) => !p.path && !p.error);
  const failed = picks.filter((p) => p.error);
  const done = picks.filter((p) => p.path);
  useEffect(() => {
    if (!uploading) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [uploading]);

  async function startUpload(list: Pick[]) {
    const release = await keepAwake();
    try {
      for (const p of list) {
        try {
          const path = await uploadRequestFile(p.file, p.folder, (pct) =>
            setPicks((xs) => xs.map((x) => (x.key === p.key ? { ...x, pct } : x))));
          setPicks((xs) => xs.map((x) => (x.key === p.key ? { ...x, path, pct: 100, error: undefined } : x)));
        } catch {
          setPicks((xs) => xs.map((x) => (x.key === p.key ? { ...x, error: 'failed' } : x)));
        }
      }
    } finally { release(); }
  }

  function addFiles(files: FileList | null) {
    if (!files?.length) return;
    const list = Array.from(files).map((file) => ({ key: `${file.name}:${file.size}:${file.lastModified}:${Math.random()}`, file, folder, pct: 0 }));
    setPicks((xs) => [...xs, ...list]);
    void startUpload(list);
  }

  const canSend = kind === 'general'
    ? text.trim().length >= 3
    : title.trim().length >= 3 && (text.trim().length > 0 || !!voice) && !uploading && failed.length === 0 && (done.length === 0 || consent);

  const send = useMutation({
    mutationFn: () => sendRequest({
      kind: kind!, title: kind === 'creative' ? title.trim() : undefined, brief: text.trim() || undefined,
      voice_path: voice?.path ?? null, files: done.map((p) => ({ path: p.path!, folder: p.folder })), consent_confirmed: consent,
    }),
    onSuccess: (r) => {
      try { localStorage.removeItem(DRAFT); } catch { /* fine */ }
      params.delete('new'); setParams(params, { replace: true });
      navigate(`/portal/requests/${r.id}?sent=1`);
    },
    onError: (e) => toast.error(errorText(e, 'Could not send. Try again. / تعذّر الإرسال، حاول مرة أخرى.')),
  });

  function reset() {
    setComposing(false); setKind(null); setText(''); setTitle(''); setVoice(null); setPicks([]); setConsent(false);
    try { localStorage.removeItem(DRAFT); } catch { /* fine */ }
    params.delete('new'); setParams(params, { replace: true });
  }

  const byFolder = useMemo(() => {
    const m = new Map<string, Pick[]>();
    for (const p of picks) m.set(p.folder, [...(m.get(p.folder) || []), p]);
    return m;
  }, [picks]);

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="text-xl font-semibold text-muted-strong"><Bi en="Your requests" ar="طلباتك" /></h1>
        {!composing && (
          <button onClick={() => setComposing(true)} data-testid="new-request"
            className="min-h-11 px-4 rounded-lg bg-accent-600 hover:bg-accent-500 text-white text-sm font-medium inline-flex items-center gap-2 transition">
            <Plus size={15} /> <Bi en="New request" ar="طلب جديد" />
          </button>
        )}
      </div>

      {composing && !kind && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="kind-choice">
          <button onClick={() => setKind('general')} data-testid="kind-general"
            className="text-start bg-bg-card border border-line hover:border-accent-400 rounded-2xl p-4 min-h-24 transition">
            <MessageSquare size={18} className="text-accent-400 mb-2" />
            <div className="text-sm font-semibold text-muted-strong"><Bi en="General request" ar="طلب عام" /></div>
            <p className="text-[13px] text-muted mt-1">Just type what you need.</p>
            <p className="text-[13px] text-muted" dir="rtl" lang="ar">اكتب طلبك بكل بساطة، وفريق KAF يرد عليك.</p>
          </button>
          <button onClick={() => setKind('creative')} data-testid="kind-creative"
            className="text-start bg-bg-card border border-line hover:border-accent-400 rounded-2xl p-4 min-h-24 transition">
            <Sparkles size={18} className="text-accent-400 mb-2" />
            <div className="text-sm font-semibold text-muted-strong"><Bi en="Creative request" ar="طلب محتوى" /></div>
            <p className="text-[13px] text-muted mt-1">A video or a design: the idea, the details and your files.</p>
            <p className="text-[13px] text-muted" dir="rtl" lang="ar">فيديو أو تصميم: أرسل الفكرة والتفاصيل والملفات.</p>
          </button>
          <button onClick={reset} className="sm:col-span-2 text-[13px] text-muted hover:text-muted-strong min-h-11">Cancel / إلغاء</button>
        </div>
      )}

      {composing && kind && (
        <form onSubmit={(e) => { e.preventDefault(); if (canSend) send.mutate(); }}
          className="bg-bg-card border border-bg-card rounded-2xl p-4 md:p-5 space-y-4" data-testid={`form-${kind}`}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-muted-strong">
              {kind === 'general' ? <Bi en="General request" ar="طلب عام" /> : <Bi en="Creative request" ar="طلب محتوى" />}
            </div>
            <button type="button" onClick={reset} aria-label="Close" className="h-11 w-11 inline-flex items-center justify-center text-muted hover:text-muted-strong"><X size={16} /></button>
          </div>

          {kind === 'creative' && (
            <label className="block space-y-1.5">
              <span className="text-[13px] text-muted"><Bi en="Heading" ar="العنوان" /></span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} dir="auto" data-testid="req-title"
                placeholder="e.g. Reel for the new veneers offer"
                className="w-full h-11 px-3 rounded-lg border border-line bg-bg text-sm text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400" />
            </label>
          )}

          <label className="block space-y-1.5">
            <span className="text-[13px] text-muted">
              {kind === 'general' ? <Bi en="Write what you need" ar="اكتب طلبك هنا" /> : <Bi en="Details" ar="التفاصيل" />}
            </span>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={kind === 'general' ? 5 : 4} maxLength={8000} dir="auto" data-testid="req-text"
              className="w-full px-3 py-2.5 rounded-lg border border-line bg-bg text-sm text-muted-strong placeholder:text-muted/60 focus:outline-none focus:border-accent-400 resize-y" />
          </label>

          {kind === 'creative' && (
            <>
              <div className="space-y-1.5">
                <span className="text-[13px] text-muted"><Bi en="Or record a voice note" ar="أو سجّل رسالة صوتية" /></span>
                <VoiceRecorder value={voice} onChange={setVoice} disabled={send.isPending} />
              </div>

              <div className="space-y-2">
                <span className="text-[13px] text-muted"><Bi en="Files, by folder (optional)" ar="الملفات حسب المجلد (اختياري)" /></span>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Folder">
                  {folders.map((f) => (
                    <button key={f.en} type="button" role="radio" aria-checked={folder === f.en} onClick={() => setFolder(f.en)}
                      className={cn('min-h-11 px-3 rounded-full border text-[13px] transition', folder === f.en ? 'border-accent-400 bg-accent-600/15 text-accent-400' : 'border-line text-muted hover:text-muted-strong')}>
                      {f.en} · <span lang="ar">{f.ar}</span>
                    </button>
                  ))}
                  <button type="button" onClick={() => {
                    const name = window.prompt('Folder name / اسم المجلد')?.trim();
                    if (name) { setFolders((xs) => xs.some((x) => x.en === name) ? xs : [...xs, { en: name, ar: '' }]); setFolder(name); }
                  }} className="min-h-11 px-3 rounded-full border border-dashed border-line text-[13px] text-muted inline-flex items-center gap-1.5">
                    <FolderPlus size={14} /> New folder · <span lang="ar">مجلد جديد</span>
                  </button>
                </div>
                <input ref={fileRef} type="file" multiple className="hidden" data-testid="req-files" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
                <button type="button" onClick={() => fileRef.current?.click()}
                  className="min-h-11 px-4 rounded-lg border border-line text-sm text-muted-strong hover:border-accent-400 inline-flex items-center gap-2">
                  <Plus size={14} /> <span>Add files to {folder} · <span lang="ar">إضافة ملفات</span></span>
                </button>
                {[...byFolder.entries()].map(([f, list]) => (
                  <div key={f} className="rounded-xl border border-line p-3">
                    <div className="text-[12px] uppercase tracking-wide text-muted mb-1.5">{f} ({list.length})</div>
                    <ul className="space-y-1.5">
                      {list.map((p) => (
                        <li key={p.key} className="flex items-center gap-2 text-[13px]">
                          <span className="truncate flex-1 text-muted-strong" dir="auto">{p.file.name}</span>
                          {p.error ? (
                            <button type="button" className="min-h-9 px-2 text-amber-300 inline-flex items-center gap-1"
                              onClick={() => { setPicks((xs) => xs.map((x) => x.key === p.key ? { ...x, error: undefined, pct: 0 } : x)); void startUpload([{ ...p, error: undefined }]); }}>
                              <RotateCw size={13} /> Retry / إعادة المحاولة
                            </button>
                          ) : (
                            <span className={cn('tabular-nums', p.path ? 'text-emerald-400' : 'text-muted')}>{p.path ? '✓' : `${p.pct}%`}</span>
                          )}
                          <button type="button" aria-label={`Remove ${p.file.name}`} className="h-9 w-9 inline-flex items-center justify-center text-muted hover:text-rose-400"
                            onClick={() => setPicks((xs) => xs.filter((x) => x.key !== p.key))}><X size={13} /></button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <p aria-live="polite" className="text-[13px] text-muted">
                  {uploading && <>Uploading {done.length} of {picks.length}, keep this screen open. <span dir="rtl" lang="ar">جارٍ رفع {done.length} من {picks.length}، لا تغلق الصفحة.</span></>}
                </p>
                {done.length > 0 && (
                  <label className="flex items-start gap-2.5 text-[13px] text-muted-strong cursor-pointer min-h-11" data-testid="req-consent">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-5 w-5 accent-accent-400" />
                    <span>Everyone shown in these files agreed in writing to KAF using them in ads.
                      <span className="block text-muted" dir="rtl" lang="ar">كل من يظهر في هذه الملفات وافق كتابيًا على استخدامها في إعلانات KAF.</span></span>
                  </label>
                )}
              </div>
            </>
          )}

          <button type="submit" disabled={!canSend || send.isPending} data-testid="req-send"
            className="min-h-11 px-5 rounded-lg bg-accent-600 hover:bg-accent-500 text-white text-sm font-medium disabled:opacity-50 transition">
            {send.isPending ? 'Sending... / جارٍ الإرسال' : 'Send request / إرسال الطلب'}
          </button>
        </form>
      )}

      {q.isLoading && <div className="text-sm text-muted py-10 text-center">Loading... / جارٍ التحميل</div>}
      {q.isError && (
        <div className="text-sm text-muted py-10 text-center">
          Could not load, try again. <span lang="ar">تعذّر التحميل، حاول مرة أخرى.</span>
          <button onClick={() => q.refetch()} className="ms-2 min-h-11 px-3 text-accent-400">Retry</button>
        </div>
      )}
      <div className="space-y-3">
        {(q.data ?? []).map((r) => <RequestRow key={r.id} r={r} />)}
        {q.data && q.data.length === 0 && !composing && (
          <div className="border border-dashed border-bg-card rounded-2xl py-14 text-center text-sm text-muted">
            <Bi en="No requests yet." ar="لا توجد طلبات بعد." />
          </div>
        )}
      </div>
    </div>
  );
}

function RequestRow({ r }: { r: ClientRequest }) {
  const s = STATUS[r.status] ?? STATUS.new;
  const yourTurn = r.status === 'new' && r.waiting_on === 'clinic';
  return (
    <Link to={`/portal/requests/${r.id}`} data-testid="request-row"
      className={cn('block bg-bg-card border rounded-xl px-4 py-3.5 space-y-1.5 hover:border-accent-400/50 transition', yourTurn ? 'border-amber-400/60' : 'border-bg-card')}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-medium text-muted-strong" dir="auto">{r.title}</div>
        <span className={cn('text-[12px] px-2 py-0.5 rounded-full', s.tone)}>{s.en} · <span lang="ar">{s.ar}</span></span>
      </div>
      <div className="flex items-center gap-3 flex-wrap text-[12px] text-muted">
        <span>{r.kind === 'creative' ? 'Creative · محتوى' : 'General · عام'}</span>
        {r.file_count > 0 && <span>{r.file_count} files</span>}
        {yourTurn && <span className="text-amber-300 font-medium">KAF replied, your turn · <span lang="ar">رد فريق KAF، بانتظار ردك</span></span>}
        {r.status === 'new' && r.waiting_on === 'kaf' && <span>Waiting for KAF · <span lang="ar">بانتظار رد KAF</span></span>}
        {r.unread > 0 && <span className="px-1.5 rounded-full bg-accent-600 text-white tabular-nums" aria-label={`${r.unread} new`}>{r.unread}</span>}
        <span className="ms-auto">{new Date(r.created_at).toLocaleDateString()}</span>
      </div>
    </Link>
  );
}
